import { MapCursorMode } from '@components/ui/map/formMap/useMapCursor';
import { useMapSelectedPositionState } from '@hooks/useMapSelectedPositionState';
import { DrawMode } from '@mapbox/mapbox-gl-draw';
import { bbox } from '@turf/turf';
import { addCoordinate, deleteCoordinate, updateCoordinate } from '@utils/map/polygon.utils';
import { isValidCoordinates } from '@utils/validation/coordinates.utils';
import { MultiPolygon, Point, Polygon } from 'geojson';
import { createContext, PropsWithChildren, RefObject, useCallback, useContext, useMemo, useState } from 'react';
import { LngLatBoundsLike, MapRef } from 'react-map-gl/mapbox';

import { Coordinates, CoordinatesUnit, CoordinatesUnitEnum, NullableCoordinates } from '@/types/commons/commons.types';

function parsePolygonFieldName(
  fieldName: string,
): { ringIndex: number; pointIndex: number; coordIndex: number } | null {
  const match = new RegExp(/polygons?\.coordinates(?:\[\d+])?\[(\d+)]\[(\d+)]\[(\d+)]/).exec(fieldName);
  if (!match) return null;

  const ringIndex = Number(match[1]);
  const pointIndex = Number(match[2]);
  const coordIndex = Number(match[3]);

  return { ringIndex, pointIndex, coordIndex };
}

function isPolygonGeometry(
  geometry: EditableGeometry | undefined,
  drawMode: FormMapDrawMode,
): geometry is Polygon | MultiPolygon {
  return drawMode === 'draw_polygon' && (geometry?.type === 'Polygon' || geometry?.type === 'MultiPolygon');
}

export type EditableGeometry = Point | Polygon | MultiPolygon;
export type FormMapDrawMode = Extract<DrawMode, 'draw_polygon' | 'draw_point'>;
type FormMapContextProps = {
  mapRef: RefObject<MapRef | null>;
  mapConstraintsRef: RefObject<HTMLDivElement | null>;
  initialGeometry: EditableGeometry | undefined;
  cursorMode: MapCursorMode;
  drawMode: FormMapDrawMode;
};

type BaseFormMapContext = FormMapContextProps & {
  unit: CoordinatesUnit;
  setUnit: (v: CoordinatesUnit) => void;

  mouseSelectedPosition: NullableCoordinates;
  isSelectionEnabled: boolean;
  setSelectionOn: (active: boolean) => void;
  setSelectedPosition: (position: Coordinates) => void;

  geometry: EditableGeometry | undefined;
  updateGeometry: (geometry: EditableGeometry) => void;
  isPolygonal: (geometry: EditableGeometry | undefined) => geometry is Polygon | MultiPolygon;
};

type PolygonFormMapContext = BaseFormMapContext & {
  drawMode: 'draw_polygon';

  zoomOnPolygon: (polygon: Polygon) => void;
  addPolygonCoordinate: (polygonIndex?: number, ringIndex?: number) => void;
  updatePolygonCoordinate: (field: string, value: string, polygonIndex?: number, ringIndex?: number) => void;
  deletePolygonCoordinate: (pointIndex: number, polygonIndex?: number, ringIndex?: number) => void;
};

type PointFormMapContext = BaseFormMapContext & {
  drawMode: 'draw_point';
  zoomOnPoint: (coordinates: Coordinates) => void;
};

type FormMapContextType = PolygonFormMapContext | PointFormMapContext;

const FormMapContext = createContext<FormMapContextType | null>(null);

export const FormMapProvider = ({
  mapRef,
  mapConstraintsRef,
  initialGeometry,
  cursorMode,
  drawMode,
  children,
}: PropsWithChildren<FormMapContextProps>) => {
  const [unit, setUnit] = useState<CoordinatesUnit>(CoordinatesUnitEnum.DECIMAL_DEGREES);
  const [editableGeometry, setEditableGeometry] = useState<EditableGeometry | undefined>(initialGeometry);

  const { positionState, setSelectionOn, setSelectedPosition } = useMapSelectedPositionState();

  const updateGeometry = useCallback((geometry: EditableGeometry) => {
    setEditableGeometry(geometry);
  }, []);

  const isPolygonal = (geometry: EditableGeometry | undefined) => {
    return geometry?.type === 'Polygon' || geometry?.type === 'MultiPolygon';
  };

  const updatePolygonCoordinate = useCallback(
    (field: string, value: string, polygonIndex = 0, ringIndex = 0) => {
      if (!isPolygonGeometry(editableGeometry, drawMode)) return;

      const parsed = parsePolygonFieldName(field);
      if (!parsed) return;

      const { pointIndex, coordIndex } = parsed;
      const next = updateCoordinate(
        editableGeometry as Polygon | MultiPolygon,
        pointIndex,
        coordIndex,
        Number(value),
        polygonIndex,
        ringIndex,
      );

      setEditableGeometry(next);
    },
    [drawMode, editableGeometry],
  );

  const deletePolygonCoordinate = useCallback(
    (pointIndex: number, polygonIndex = 0, ringIndex = 0) => {
      if (!isPolygonGeometry(editableGeometry, drawMode)) return;

      const next = deleteCoordinate(editableGeometry as Polygon | MultiPolygon, pointIndex, polygonIndex, ringIndex);

      setEditableGeometry(next);
    },
    [drawMode, editableGeometry],
  );

  const addPolygonCoordinate = useCallback(
    (polygonIndex = 0, ringIndex = 0) => {
      if (!isPolygonGeometry(editableGeometry, drawMode)) return;

      const next = addCoordinate(editableGeometry as Polygon | MultiPolygon, polygonIndex, ringIndex);

      setEditableGeometry(next);
    },
    [drawMode, editableGeometry],
  );

  const zoomOnPolygon = useCallback(
    (polygon: Polygon) => {
      const map = mapRef.current?.getMap();
      if (!map) return;

      const bounds = bbox(polygon);
      map.fitBounds(bounds as LngLatBoundsLike, {
        padding: 40,
        maxZoom: 16,
        duration: 500,
      });
    },
    [mapRef],
  );

  const zoomOnPoint = useCallback(
    (coordinates: NullableCoordinates) => {
      const map = mapRef.current?.getMap();
      if (!map || !isValidCoordinates(coordinates)) return;

      map.flyTo({
        center: [coordinates.longitude!, coordinates.latitude!],
        zoom: map.getZoom(),
      });
    },
    [mapRef],
  );

  const value = useMemo(
    () =>
      drawMode === 'draw_polygon'
        ? {
            cursorMode,
            mapRef,
            mapConstraintsRef,
            initialGeometry,
            editableGeometry,
            ...positionState,
            setSelectionOn,
            setSelectedPosition,
            zoomOnPolygon,
            unit,
            setUnit,
            drawMode,

            geometry: editableGeometry,
            setGeometry: setEditableGeometry,
            updateGeometry,
            isPolygonal,

            updatePolygonCoordinate,
            deletePolygonCoordinate,
            addPolygonCoordinate,
          }
        : {
            cursorMode,
            mapRef,
            mapConstraintsRef,
            initialGeometry,
            editableGeometry,
            ...positionState,
            setSelectionOn,
            setSelectedPosition,
            unit,
            setUnit,
            drawMode,
            zoomOnPoint,

            geometry: editableGeometry,
            setGeometry: setEditableGeometry,
            updateGeometry,
            isPolygonal,
          },
    [
      drawMode,
      cursorMode,
      mapRef,
      mapConstraintsRef,
      initialGeometry,
      editableGeometry,
      positionState,
      setSelectionOn,
      setSelectedPosition,
      zoomOnPolygon,
      unit,
      updateGeometry,
      updatePolygonCoordinate,
      deletePolygonCoordinate,
      addPolygonCoordinate,
      zoomOnPoint,
    ],
  );

  return <FormMapContext value={value}>{children}</FormMapContext>;
};

export const useFormMapContext = (): FormMapContextType => {
  const context = useContext(FormMapContext);
  if (!context) {
    throw new Error('useFormMapContext must be used within a FormMapProvider');
  }
  return context;
};

export const usePolygonFormMapContext = (): PolygonFormMapContext => {
  const context = useFormMapContext();

  if (context.drawMode !== 'draw_polygon') {
    throw new Error('usePolygonFormMapContext can only be used with draw_polygon mode');
  }

  return context;
};

export const usePointFormMapContext = (): PointFormMapContext => {
  const context = useFormMapContext();

  if (context.drawMode !== 'draw_point') {
    throw new Error('usePointFormMapContext can only be used with draw_point mode');
  }

  return context;
};
